import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { MacroToolsStrip } from "@/components/site/MacroToolsStrip";
import { useState } from "react";
import { GoldSessionsClock, SessionHoursTable, type SessionRow, type ZoneKey } from "@/components/tools/GoldSessionsClock";

const SITE = "https://xauproacademy.com";
const PATH = "/gold-trading-sessions";
const TITLE = "Gold Trading Hours: Best Time to Trade | XAU Pro Academy";
const DESC =
  "Live gold market hours in your local time. When XAUUSD opens in New York, London and Tokyo, the London and New York overlap, the best time to trade gold, and today's news freeze windows.";

const FAQ = [
  {
    q: "What are gold trading hours?",
    a: "Spot gold (XAUUSD) trades almost 24 hours a day, five days a week: from Sunday around 22:00 UTC to Friday around 21:00 UTC, with a short daily break near 21:00 to 22:00 UTC at most brokers.",
  },
  {
    q: "What is the best time to trade gold?",
    a: "The London and New York overlap, roughly 12:00 to 16:00 UTC (13:00 to 17:00 London time), carries the highest gold volume and the tightest spreads. The London open is the second strongest window.",
  },
  {
    q: "What time does the gold market open in New York?",
    a: "The New York session opens at 08:00 New York time, with US cash equities following at 09:30. Gold futures on COMEX trade almost around the clock, but New York participation starts in earnest at 08:00 and US data is released at 08:30.",
  },
  {
    q: "What time does the gold market open on Sunday?",
    a: "Most brokers reopen spot gold on Sunday evening, around 18:00 New York time (22:00 or 23:00 UTC depending on daylight saving). The first hour is thin and can gap from Friday's close.",
  },
  {
    q: "When is the London and New York overlap?",
    a: "It starts when New York opens at 08:00 New York time and ends when London closes at 17:00 London time. Daylight saving changes shift it by an hour for a few weeks each spring and autumn, which the clock above handles automatically.",
  },
  {
    q: "Which session is most volatile for gold?",
    a: "The London and New York overlap, especially around 08:30 New York time when US data such as CPI and NFP is released. The London open is the second most active window. The Asian session is usually the quietest.",
  },
  {
    q: "Why should I avoid trading gold during the Asian session?",
    a: "Participation is thinner, so gold usually builds a range rather than trending. Breakouts in Asian hours fail often, and the range high and low become the liquidity London later targets.",
  },
  {
    q: "What is a news freeze window?",
    a: "A period around high impact US releases such as CPI, NFP and FOMC where spreads widen and slippage rises. The tool marks 15 minutes before to 30 minutes after each release; technical setups lose their edge inside it.",
  },
  {
    q: "Is gold open on weekends?",
    a: "No. Gold closes Friday evening and reopens Sunday evening UTC. Prices can gap at the reopen, so positions held over the weekend carry gap risk.",
  },
];

const PROFILE = [
  { s: "Asian (Tokyo)", activity: "Low", spread: "Wider", behaviour: "Range building. The high and low become liquidity targets.", action: "Mark the range, avoid breakouts." },
  { s: "London open", activity: "High", spread: "Tightening", behaviour: "First sweep of the Asian range, often a false move.", action: "Wait for the sweep and displacement." },
  { s: "London / NY overlap", activity: "Highest", spread: "Tightest", behaviour: "Main delivery leg, US data at 08:30 New York.", action: "Primary execution window, respect news freeze." },
  { s: "New York afternoon", activity: "Declining", spread: "Widening late", behaviour: "Follow through fades, positioning squares.", action: "Manage open trades, avoid new risk late." },
];

const NEXT = [
  { to: "/xauusd-volatility-heatmap", label: "Timing edge", title: "XAUUSD volatility heatmap", body: "Average gold range for every weekday and UTC hour, from the last 90 days of spot data." },
  { to: "/position-size-calculator", label: "Size the trade", title: "Gold lot size calculator", body: "Turn your stop distance into the correct XAUUSD lot size before the session opens." },
  { to: "/xauusd-analysis-today", label: "Read today", title: "XAUUSD analysis today", body: "Live price, session context and the current liquidity map for gold." },
  { to: "/gold-inflation-cpi", label: "News risk", title: "CPI and gold tracker", body: "How US inflation releases have moved gold, and when the next one lands." },
  { to: "/gold-nfp-employment", label: "News risk", title: "NFP and gold tracker", body: "Jobs report history and its effect on XAUUSD in the overlap window." },
  { to: "/best-prop-firms-for-gold", label: "Funded accounts", title: "Best prop firms for gold", body: "News rules, weekend holding and drawdown types that matter for session traders." },
  { to: "/prop-firm-gold", label: "Funded accounts", title: "Prop firm gold trading guide", body: "Passing an evaluation on XAUUSD without breaching the daily loss limit." },
  { to: "/gold-cot-report", label: "Positioning", title: "Gold COT report", body: "Weekly institutional positioning behind the moves you see in each session." },
  { to: "/signals", label: "Desk", title: "Live desk signals", body: "Setups qualified only inside liquid sessions and outside news freeze windows." },
] as const;

export const Route = createFileRoute("/gold-trading-sessions")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      {
        name: "keywords",
        content:
          "gold trading hours, gold market hours, what time does gold market open, gold market open time new york, gold market open sunday, most volatile time for gold, gold volatility by session, xauusd trading hours, best time to trade gold, gold market open time, forex market hours, london new york overlap, gold trading sessions, trading sessions clock, gold news times",
      },
      { property: "og:title", content: "Gold Trading Hours: Live Sessions and News Freeze Clock" },
      {
        property: "og:description",
        content: "When XAUUSD is open in your time zone, the best time to trade gold, and today's news risk windows.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}${PATH}` },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Gold Trading Sessions and News Freeze Clock",
          url: `${SITE}${PATH}`,
          applicationCategory: "FinanceApplication",
          operatingSystem: "Any",
          description: DESC, featureList: ["Live session clock in UTC", "London and New York overlap", "High impact news freeze windows"], browserRequirements: "Requires JavaScript. Modern HTML5 browser", softwareVersion: "2026.1", isAccessibleForFree: true, inLanguage: "en",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD", availability: "https://schema.org/OnlineOnly" },
          publisher: { "@type": "Organization", name: "XAU Pro Academy", url: SITE },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Market Tools", item: `${SITE}/position-size-calculator` },
            { "@type": "ListItem", position: 3, name: "Gold Trading Sessions", item: `${SITE}${PATH}` },
          ],
        }),
      },
    ],
  }),
  component: GoldSessionsPage,
});

const TOK="Asia/Tokyo",LDN="Europe/London",NY="America/New_York";
const TABLE: SessionRow[] = [
  { name: "Asian (Tokyo)", startUtc: 0, endUtc: 9, start: { zone: TOK, h: 9 }, end: { zone: TOK, h: 18 }, liquidity: "Low", note: "Range building. Mark the high and low." },
  { name: "London open", startUtc: 8, endUtc: 11, start: { zone: LDN, h: 8 }, end: { zone: LDN, h: 11 }, liquidity: "High", note: "First liquidity sweep, often a Judas Swing." },
  { name: "London / NY overlap", startUtc: 13, endUtc: 17, start: { zone: NY, h: 8 }, end: { zone: LDN, h: 17 }, liquidity: "Highest", note: "Main delivery window, US data releases." },
  { name: "New York afternoon", startUtc: 17, endUtc: 22, start: { zone: LDN, h: 17 }, end: { zone: NY, h: 17 }, liquidity: "Declining", note: "Follow through fades, spreads widen late." },
];

function GoldSessionsPage() {
  const [zone, setZone] = useState<ZoneKey>("local");
  return (
    <Layout>
      <section className="container mx-auto max-w-7xl px-4 pt-14 md:pt-20">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Free session tool</div>
        <h1 className="mt-3 max-w-3xl font-display text-3xl font-semibold leading-tight md:text-5xl">
          Gold Trading Hours: Live Market Sessions Clock
        </h1>
        <p className="mt-4 max-w-2xl text-muted-foreground md:text-lg">
          See which sessions are open in your local time, when the London and New York overlap starts, and where
          today's high impact news makes gold untradeable.
        </p>
      </section>

      <section className="container mx-auto mt-10 max-w-7xl px-4">
        <GoldSessionsClock zone={zone} onZoneChange={setZone} />
        <p className="mt-3 text-[11px] text-muted-foreground">
          Session times follow each financial centre's local hours and adjust for daylight saving automatically. The liquidity profile is illustrative.
        </p>
      </section>

      <section className="container mx-auto mt-16 max-w-4xl px-4">
        <h2 className="font-display text-2xl font-semibold md:text-3xl">XAUUSD trading hours by session</h2>
        <SessionHoursTable rows={TABLE} zone={zone} />

        <h2 className="mt-12 font-display text-2xl font-semibold md:text-3xl">The best time to trade gold</h2>
        <div className="mt-4 space-y-4 text-muted-foreground">
          <p>Gold moves most when London and New York are both open. Volume, institutional order flow and US macro data concentrate in those four hours, which is why spreads are tightest and follow through is strongest.</p>
          <p>The classic daily sequence is Asian accumulation, London manipulation, New York distribution. Read the full breakdown in our guide to <Link to="/education/$slug" params={{ slug: "gold-trading-sessions-london-new-york" }} className="text-gold hover:underline">gold trading sessions in London and New York</Link>, and the <Link to="/education/$slug" params={{ slug: "amd-pattern-gold-daily-cycles" }} className="text-gold hover:underline">AMD pattern in gold daily cycles</Link>.</p>
          <p>Session timing works with structure, not instead of it. London's opening move is often a <Link to="/education/$slug" params={{ slug: "liquidity-sweep-gold-trading" }} className="text-gold hover:underline">liquidity sweep</Link> of the Asian range, so wait for <Link to="/education/$slug" params={{ slug: "ict-displacement-explained" }} className="text-gold hover:underline">displacement</Link> before committing.</p>
          <p>For the narrower ICT windows inside these sessions, including the London and New York killzones and the Silver Bullet hours, use the live <Link to="/gold-killzones-clock" className="text-gold hover:underline">gold killzones clock</Link>.</p>
        </div>

        <h2 className="mt-12 font-display text-2xl font-semibold md:text-3xl">The London and New York overlap: the main window</h2>
        <div className="mt-4 space-y-4 text-muted-foreground">
          <p>For roughly four hours each day, the two largest gold dealing centres are open together. London carries the spot and OTC flow, New York brings futures, US macro data and institutional rebalancing. Liquidity is deepest, spreads are tightest and moves tend to follow through instead of reversing.</p>
          <p>The overlap usually starts at 08:00 New York time and ends at the 17:00 London close. In UTC that is 12:00 to 16:00 in northern summer and 13:00 to 17:00 in winter. For two to three weeks in March and late October the US and UK change clocks on different dates, and the overlap stretches or shrinks by an hour. The clock above follows each city's local hours, so it stays correct through those weeks.</p>
          <p>Most high impact US releases land at 08:30 New York time, inside this window. Read the <Link to="/education/$slug" params={{ slug: "high-probability-entry-criteria-gold" }} className="text-gold hover:underline">high probability entry criteria for gold</Link> before trading the overlap, and plan entries from <Link to="/education/$slug" params={{ slug: "premium-discount-zones-gold" }} className="text-gold hover:underline">premium and discount zones</Link> rather than chasing the release candle.</p>
        </div>

        <h2 className="mt-12 font-display text-2xl font-semibold md:text-3xl">Gold volatility by session</h2>
        <p className="mt-4 text-muted-foreground">An observational profile of how XAUUSD typically behaves in each session. Exact ranges change with the volatility regime, so treat it as a guide, not a forecast.</p>
        <div className="mt-5 overflow-x-auto rounded-xl border border-border/60 bg-card/60">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              <tr className="border-b border-border/60">
                <th className="p-3 text-left">Session</th><th className="p-3 text-left">Activity</th><th className="p-3 text-left">Spread</th><th className="p-3 text-left">Typical behaviour</th><th className="p-3 text-left">Desk approach</th>
              </tr>
            </thead>
            <tbody>
              {PROFILE.map((r) => (
                <tr key={r.s} className="border-b border-border/40 last:border-0">
                  <td className="p-3 font-medium text-foreground">{r.s}</td>
                  <td className="p-3 text-gold">{r.activity}</td>
                  <td className="p-3 text-muted-foreground">{r.spread}</td>
                  <td className="p-3 text-muted-foreground">{r.behaviour}</td>
                  <td className="p-3 text-muted-foreground">{r.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="mt-12 font-display text-2xl font-semibold md:text-3xl">When the gold market opens and closes</h2>
        <div className="mt-4 space-y-4 text-muted-foreground">
          <p><span className="text-foreground">Sunday open:</span> spot gold reopens around 18:00 New York time. The first hour is thin, and any weekend news is priced as a gap.</p>
          <p><span className="text-foreground">Daily break:</span> most brokers pause XAUUSD for about an hour after 17:00 New York time while the trading day rolls. Spreads widen sharply around the roll, so avoid entries and tight stops there.</p>
          <p><span className="text-foreground">Friday close:</span> trading stops at 17:00 New York time. Funded traders should check weekend holding rules in our <Link to="/best-prop-firms-for-gold" className="text-gold hover:underline">prop firm comparison for gold</Link>.</p>
        </div>

        <h2 className="mt-12 font-display text-2xl font-semibold md:text-3xl">News freeze: when not to trade gold</h2>
        <div className="mt-4 space-y-4 text-muted-foreground">
          <p>CPI, NFP and FOMC decisions can move gold $20 or more in seconds. Stops slip and spreads multiply. The clock above shades each high impact release from 15 minutes before to 30 minutes after.</p>
          <p>See how these releases have moved gold historically in <Link to="/gold-inflation-cpi" className="text-gold hover:underline">US CPI and gold</Link> and <Link to="/gold-nfp-employment" className="text-gold hover:underline">NFP and gold</Link>. Once the window clears, size the trade with the <Link to="/position-size-calculator" className="text-gold hover:underline">gold position size calculator</Link>.</p>
          <p>On a funded account, one news spike can breach the daily loss rule. Our <Link to="/prop-firm-gold" className="text-gold hover:underline">prop firm gold trading guide</Link> covers news rules in detail.</p>
        </div>
      </section>

      <section className="container mx-auto mt-16 max-w-7xl px-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Next step</div>
        <h2 className="mt-3 font-display text-2xl font-semibold md:text-3xl">You know the session. Now plan the trade.</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {NEXT.map((n) => (
            <Link key={n.to} to={n.to} className="group rounded-xl border border-border/60 bg-card/60 p-5 transition-colors hover:border-gold/50">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{n.label}</div>
              <div className="mt-2 font-medium text-foreground group-hover:text-gold">{n.title}</div>
              <p className="mt-2 text-sm text-muted-foreground">{n.body}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="container mx-auto mt-16 max-w-7xl px-4">
        <div className="rounded-2xl border border-gold/30 bg-card/70 p-6 md:p-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Timing is one filter</div>
          <h2 className="mt-3 max-w-2xl font-display text-2xl font-semibold md:text-3xl">
            The clock tells you when. The desk tells you whether.
          </h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Orion only qualifies setups inside liquid sessions and pauses around high impact releases, so every signal already respects this clock.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild variant="gold">
              <Link to="/signals">See the Signals Desk <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/xauusd-analysis-today">XAUUSD analysis today</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="container mx-auto mt-16 max-w-4xl px-4 pb-6">
        <h2 className="font-display text-2xl font-semibold md:text-3xl">Frequently asked questions</h2>
        <div className="mt-6 divide-y divide-border/60 rounded-xl border border-border/60 bg-card/60">
          {FAQ.map((f) => (
            <details key={f.q} className="group p-5">
              <summary className="cursor-pointer list-none font-medium text-foreground">{f.q}</summary>
              <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <MacroToolsStrip />
      <div className="pb-16" />
    </Layout>
  );
}
